import React, { useCallback, useEffect, useState } from 'react';
import { AddressType, Address } from '../types/Address';
import AddressCard from './AddressCard';
import Dialog from '../../common/components/Dialog';
import AddressModal from './modals/AddressModal';
import { getAddresses } from '../services/AddressServices';
import useClickOutside from '../hooks/useModalClickHandler';
import { convertAddressData } from '../../utils/convertAddressData';
import { useTranslations } from '../../localization/useTranslations';
import { useAddresses } from '../hooks/useAddresses';
import EmptyPlaceholder from '../../common/components/EmptyPlaceholder';
import { getDeliveryAddressType } from '../../utils/getDeliveryAddressType';

interface AddressesProps {
  userEmail: string;
  setSnackbarMessage: (message: string) => void;
  setIsSnackbarOpen: (value: boolean) => void;
}

const Addresses: React.FC<AddressesProps> = ({ userEmail, setIsSnackbarOpen, setSnackbarMessage }) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [currentAddressType, setCurrentAddressType] = useState<AddressType | null>(null);
  const [isNewAddress, setIsNewAddress] = useState<boolean>(true);
  const [isEditDropdownOpen, setIsEditDropdownOpen] = useState<boolean>(false);
  const [isSectionLoading, setIsSectionLoading] = useState<boolean>(true);

  const translations = useTranslations();
  const { renderAddressLabelByType } = useAddresses();

  const toggleEditDropdown = () => {
    setIsEditDropdownOpen((prevState) => !prevState);
  };

  const handleModalOpen = (addressType?: AddressType) => {
    if (addressType) {
      setCurrentAddressType(addressType);
    } else {
      const deliveryType = getDeliveryAddressType(window.country || 'cz');
      const addressTypes: AddressType[] = [deliveryType, 'shop', 'invoice'];

      const nonExistingAddressType = addressTypes.find((type) => !addresses.some((address) => address.type === type));

      setCurrentAddressType(nonExistingAddressType || deliveryType);
    }

    setIsModalOpen(true);
  };

  const handleAddressChange = (newAddress: Address) => {
    setSnackbarMessage(translations.myAccountSettings.addressSuccessfullyUpdated || 'Address updated successfully.');

    setAddresses(
      (prevAddresses) =>
        prevAddresses?.map((address) =>
          address.type === currentAddressType ? { ...address, ...newAddress } : address
        ) ?? [newAddress]
    );

    setIsSnackbarOpen(true);
  };

  const handleAddNewAddress = (newAddress: Address) => {
    setSnackbarMessage(translations.myAccountSettings.addressSuccessfullyAdded || 'Address added successfully.');

    // a 2nd check here to make sure the address is not already in the list
    if (!addresses.some((addresses) => addresses.type === newAddress.type)) {
      setAddresses((prevAddresses) => [...prevAddresses, newAddress]);
    }

    setIsSnackbarOpen(true);
  };

  const fetchAddresses = useCallback(async () => {
    try {
      await getAddresses(userEmail).then((response) => {
        if (response.status === 'ok') {
          const deliveryType = getDeliveryAddressType(window.country);
          const validAddressTypes: AddressType[] = [deliveryType, 'shop', 'invoice'];

          const validAddresses = convertAddressData(response.addresses).filter((address) =>
            validAddressTypes.includes(address.type as AddressType)
          );

          setAddresses(validAddresses);
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSectionLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const dropdownRef = React.useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => {
    setIsEditDropdownOpen(false);
  });

  useEffect(() => {
    if (addresses) {
      setIsNewAddress(!addresses.some((address) => address.type === currentAddressType));
    }
  }, [addresses, currentAddressType]);

  const renderDropdownOpenActions = () => {
    if (addresses.length === 3) {
      return <span>{translations.myAccountSettings.edit || 'Edit'}</span>;
    } else if (addresses.length > 0 && addresses.length < 3) {
      return (
        <>
          <span>
            {translations.myAccountSettings.edit || 'Edit'}
            {' / '}
          </span>
          <span>{translations.myAccountSettings.add || 'Add'}</span>
        </>
      );
    } else {
      return <span>{translations.myAccountSettings.add || 'Add'}</span>;
    }
  };

  if (isSectionLoading) {
    return <div className="my-settings__section my-settings__section--addresses" style={{ height: '200px' }} />;
  }

  return (
    <div className="my-settings__section my-settings__section--addresses">
      <div className="my-settings__section-header">
        <h4 className="my-settings__section-title">{translations.myAccountSettings.addresses || 'Addresses'}</h4>

        <div
          ref={dropdownRef}
          className="my-settings__button-wrapper my-settings__button-wrapper--address"
          onClick={toggleEditDropdown}
        >
          <button
            id="edit-addresses"
            data-for="addresses"
            className={`my-settings__edit-button my-settings__edit-button--address link link--medium ${
              isEditDropdownOpen ? 'opened' : ''
            }`}
          >
            {renderDropdownOpenActions()}
          </button>

          <div className={`my-settings__addresses-dropdown ${isEditDropdownOpen ? 'opened' : ''}`}>
            {addresses?.map((address) => (
              <button
                key={address.type}
                className="my-settings__addresses-dropdown-button link-14"
                onClick={() => handleModalOpen(address.type as AddressType)}
              >
                {translations.myAccountSettings.edit || 'Edit'} {renderAddressLabelByType(address.type as AddressType)}
              </button>
            ))}

            {addresses.length < 3 && (
              <button
                id="add-new-address"
                className="my-settings__addresses-dropdown-button link-14"
                onClick={() => handleModalOpen()}
              >
                + {translations.myAccountSettings.addNewAddress || 'Add new address'}
              </button>
            )}
          </div>
        </div>
      </div>

      {addresses.length > 0 ? (
        <div className="my-settings__section-content my-settings__section-content--addresses">
          {addresses.map((address) => (
            <AddressCard key={address.type} address={address} />
          ))}
        </div>
      ) : (
        <EmptyPlaceholder
          text={translations.myAccountSettings.noAddresses || 'You do not have any addresses saved yet'}
        />
      )}
      <Dialog
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
      >
        <AddressModal
          onClose={() => setIsModalOpen(false)}
          addresses={addresses}
          onAddressChange={handleAddressChange}
          onAddNewAddress={handleAddNewAddress}
          currentAddressType={currentAddressType}
          setCurrentAddressType={setCurrentAddressType}
          isNewAddress={isNewAddress}
          userEmail={userEmail}
        />
      </Dialog>
    </div>
  );
};

export default Addresses;
